import { useSearchTypeFromRoute } from '@aibees/elephant/hooks';
import { FeatureFlags, SearchType } from '@aibees/elephant/types';
import { Icon } from '@aibees/shared/components';
import { classnames } from '@aibees/shared/utils';
import {
  Button,
  Menu,
  MenuItem,
  Stack,
  Tooltip,
  Typography,
} from '@mui/material';
import { memo, useState } from 'react';
import { EnrichEmailDialog } from '../enrich-email-dialog';
import { EnrichJobChangeDialog } from '../enrich-job-change-dialog';
import { EnrichPhoneNumberDialog } from '../enrich-phone-number-dialog/enrich-phone-number-dialog';
import { EnrichProfileInsightsDialog } from '../enrich-profile-insights-dialog/enrich-profile-insights-dialog';
import { ReVerifyEmailDialog } from '../re-verify-email-dialog';
import { UpdateProfileInsightsDialog } from '../update-profile-insights-dialog/update-profile-insights-dialog';
import * as Styled from './styles';
import * as SX from './sx';
import { EnrichButtonDialogType, EnrichButtonProps as Props } from './types';
// eslint-disable-next-line @nx/enforce-module-boundaries
import { useFeatureFlagsContext } from '@aibees/shared/feature-flags';
import { useConfig } from './use-config';

export const EnrichButton = memo(
  ({ className, disabled, isSmall, table }: Props) => {
    const type = useSearchTypeFromRoute();

    const isEnabled = useFeatureFlagsContext().isFeatureEnabled(
      FeatureFlags.PEOPLE_ENRICHMENT
    );

    const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
    const [dialogType, setDialogType] = useState<EnrichButtonDialogType | null>(
      null
    );

    const [activeEnrichments, setActiveEnrichments] = useState<
      EnrichButtonDialogType[]
    >([
      EnrichButtonDialogType.EnrichEmail,
      EnrichButtonDialogType.UpdateJobChange,
      EnrichButtonDialogType.ReVerifyEmail,
      EnrichButtonDialogType.EnrichPhoneNumber,
      EnrichButtonDialogType.UpdateProfileInsights,
      EnrichButtonDialogType.EnrichProfileInsights,
    ]);

    const items = useConfig();

    const _handleDisableEnrichment = (value: EnrichButtonDialogType) => {
      setActiveEnrichments((prev) => prev.filter((i) => i !== value));
    };

    const handleClick = (event: React.MouseEvent<HTMLButtonElement>) => {
      setAnchorEl(event.currentTarget);
    };

    const handleClose = () => {
      setAnchorEl(null);
    };

    const open = Boolean(anchorEl);

    const handleCloseDialog = () => {
      setDialogType(null);
      handleClose();
    };

    const handleOpenDialog = (value: EnrichButtonDialogType) => {
      if (activeEnrichments.find((i) => i === value)) {
        setDialogType(value);
        handleClose();
      }
    };

    if (!isEnabled || type !== SearchType.People) {
      return null;
    }

    return (
      <Styled.Wrapper
        className={classnames('EnrichButton', className)}
        sx={SX.root}
      >
        <EnrichJobChangeDialog
          open={dialogType === EnrichButtonDialogType.UpdateJobChange}
          onClose={handleCloseDialog}
          table={table}
        />
        <UpdateProfileInsightsDialog
          open={dialogType === EnrichButtonDialogType.UpdateProfileInsights}
          onClose={handleCloseDialog}
          table={table}
        />
        <EnrichProfileInsightsDialog
          open={dialogType === EnrichButtonDialogType.EnrichProfileInsights}
          onClose={handleCloseDialog}
          table={table}
        />
        <EnrichEmailDialog
          open={dialogType === EnrichButtonDialogType.EnrichEmail}
          onClose={handleCloseDialog}
          table={table}
        />
        <ReVerifyEmailDialog
          open={dialogType === EnrichButtonDialogType.ReVerifyEmail}
          onClose={handleCloseDialog}
          table={table}
        />
        <EnrichPhoneNumberDialog
          open={dialogType === EnrichButtonDialogType.EnrichPhoneNumber}
          onClose={handleCloseDialog}
          table={table}
        />
        <Tooltip
          className={classnames('SearchTableActionDialogLists', className)}
          sx={SX.root}
          title={disabled ? 'Please select some records first' : undefined}
          arrow
        >
          <>
            <Button
              variant="outlined"
              size="small"
              startIcon={<Icon name="outline-find-replace" size={18} />}
              endIcon={
                !isSmall && (
                  <Icon name={open ? 'chevron-up' : 'chevron-down'} size={18} />
                )
              }
              id="basic-button"
              aria-controls={open ? 'basic-menu' : undefined}
              aria-haspopup="true"
              aria-expanded={open ? 'true' : undefined}
              onClick={handleClick}
              disabled={disabled}
            >
              {!isSmall && (
                <Stack direction="row" alignItems="center" component="span">
                  Enrich
                </Stack>
              )}
            </Button>
            <Menu
              id="basic-menu"
              anchorEl={anchorEl}
              open={open}
              onClose={handleClose}
              MenuListProps={{
                'aria-labelledby': 'basic-button',
              }}
            >
              {items.map((x) => (
                <MenuItem
                  key={x.dialogType}
                  onClick={() => handleOpenDialog(x.dialogType!)}
                  sx={SX.menuItem(
                    !activeEnrichments.find((i) => i === x.dialogType)
                  )}
                >
                  <Stack justifyContent="center" alignItems="center">
                    {x.icon && <Icon name={x.icon} size={18} />}
                  </Stack>
                  <Typography variant="body4" color="grey.700">
                    {x.label}
                  </Typography>
                </MenuItem>
              ))}
            </Menu>
          </>
        </Tooltip>
      </Styled.Wrapper>
    );
  }
);

EnrichButton.displayName = 'EnrichButton';
